import { useEffect, useRef, useCallback } from 'react';

const VERSION_KEY = 'app_build_version';

export const useVersionCheck = (checkIntervalMs = 60 * 1000) => { // Check every 1 minute
    const intervalRef = useRef(null);

    const checkVersion = useCallback(async () => {
        try {
            // Fetch version.txt with aggressive cache-busting
            const cacheBuster = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
            const response = await fetch(`/version.txt?v=${cacheBuster}`, {
                method: 'GET',
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });

            if (!response.ok) {
                console.debug('Version check: version.txt not found');
                return;
            }

            const serverVersion = (await response.text()).trim();
            const storedVersion = localStorage.getItem(VERSION_KEY);

            console.log(`Version check: stored=${storedVersion}, server=${serverVersion}`);

            if (!storedVersion) {
                // First visit - store the version
                localStorage.setItem(VERSION_KEY, serverVersion);
                console.log('Version check: First visit, stored version:', serverVersion);
            } else if (storedVersion !== serverVersion) {
                // Version changed - new deploy detected!
                console.log('New deployment detected! Logging out and reloading...');

                // Update stored version
                localStorage.setItem(VERSION_KEY, serverVersion);

                // Clear auth storage
                localStorage.removeItem('authToken');
                localStorage.removeItem('user');
                localStorage.removeItem('company');

                // Force hard reload
                window.location.reload(true);
            }
        } catch (error) {
            console.debug('Version check failed:', error.message);
        }
    }, []);

    useEffect(() => {
        // Check immediately on mount
        checkVersion();

        // Set up periodic checking
        intervalRef.current = setInterval(checkVersion, checkIntervalMs);

        // Also check on visibility change (when user returns to tab)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkVersion();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkVersion, checkIntervalMs]);

    return { checkVersion };
};

export default useVersionCheck;
